import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type {
  FlightPlanCellResponse,
  RegularTransportMode,
  TransportSchemeDirectionResponse,
  TransportSchemeTicketResponse,
} from '@/api/generated/schemas';
import {
  TransportMode,
  TransportSchemeKind,
  TransportSchemeTicketKind,
} from '@/api/generated/schemas';
import { ConfirmDialog, Switch, toast } from '@/shared/ui';
import { serverErrorDetail } from '@/shared/lib/problemDetails';
import { Icon } from '@/shared/ui/icon';
import { Panel } from '@/shared/ui/panel';
import { SegmentedButtons } from '@/shared/ui/segmented-buttons';
import { StationChainSelector } from '../../../../components/StationChainSelector';
import { StationSelector } from '../../../../components/StationSelector';
import type { StationChainRef, StationRef } from '../../../../productsApi';
import { TimeBox } from '@/shared/ui/date-time-field';
import { SERIE_COST_CURRENCY_OPTIONS } from '../../detail/serieDetailFormSchema';
import { AirChainTable } from './components/AirChainTable';
import { ConnectedFlightsPanel } from './components/ConnectedFlightsPanel';
import { DirectionPanel } from './components/DirectionPanel';
import {
  TermFlightPlanMatrix,
  TermFlightPlanMatrixLegend,
  type MatrixLegColumn,
} from './components/TermFlightPlanMatrix';
import { TransportPickerButton } from './components/TransportPickerButton';
import { useFlightPlan } from './useFlightPlan';
import type { TransportSchemeEditor } from './useTransportSchemeEditor';
import type { TermDirectionDraft, TransportSchemeScope } from './model/types';
import { isGroupPricedTicket, withAmountForKind } from './model/types';
import './transportScheme.css';

// Prázdnou volbu doplňuje samotný select, tady se jen odfiltruje ze zdrojového číselníku.
const CURRENCY_OPTIONS: string[] = SERIE_COST_CURRENCY_OPTIONS.filter((code) => code !== '');

/** Pořadí nákladových sloupců: řetěz hlavní dopravy, pak spojené lety, autobus nakonec. */
const KIND_ORDER: Record<string, number> = {
  [TransportSchemeTicketKind.Primary]: 0,
  [TransportSchemeTicketKind.Connected]: 1,
  [TransportSchemeTicketKind.Bus]: 2,
};

interface TransportSchemeTabProps {
  editor: TransportSchemeEditor;
  productId: string;
  serieId: string;
  termId?: string;
  scope: TransportSchemeScope;
  readOnly: boolean;
  onOpenTerm: (termId: string) => void;
  onToggleOwnScheme?: (useOwn: boolean) => void;
}

/**
 * Záložka Doprava. Skládá se ze tří částí podle schváleného rozvržení (`opLayout: stacked`):
 * panely obou směrů vedle sebe, pod nimi spojené lety na plnou šířku a nakonec matice letového plánu.
 */
export function TransportSchemeTab({
  editor,
  productId,
  serieId,
  termId,
  scope,
  readOnly,
  onOpenTerm,
  onToggleOwnScheme,
}: TransportSchemeTabProps) {
  const { t } = useTranslation('venua-planner');
  const { draft, scheme, outbound, inbound, connectedFlights } = editor;

  /** Ticket čekající na potvrzení odebrání — drží se i počet ručních cen, o které se přijde. */
  const [pendingRemoval, setPendingRemoval] = useState<{
    ticketId: string;
    manualCostTermCount: number;
  } | null>(null);

  /**
   * Prohození čekající na potvrzení — drží počet termínů, které přijdou o přiřazenou trasu (čerstvý
   * ze serveru, ne z načteného schématu). `null` = na nic se neptáme.
   */
  const [pendingFlip, setPendingFlip] = useState<number | null>(null);

  /** Běží kontrola dopadu prohození — tlačítko je do její odpovědi zamčené. */
  const [isCheckingFlip, setCheckingFlip] = useState(false);

  const flightPlan = useFlightPlan({
    productId,
    serieId,
    termId,
    scope,
    tickets: draft?.tickets ?? [],
  });

  const ticketsById = useMemo(
    () => new Map((draft?.tickets ?? []).map((ticket) => [ticket.ticketId, ticket])),
    [draft]
  );

  const usedTransportIds = useMemo(
    () =>
      (draft?.tickets ?? [])
        .map((ticket) => ticket.regularTransportId)
        .filter((id): id is number => id != null),
    [draft]
  );

  const cellsByKey = useMemo(() => {
    const map = new Map<string, FlightPlanCellResponse>();
    flightPlan.cells.forEach((cell) => {
      map.set(`${cell.ticketId}:${cell.facilityId}:${cell.termId}`, cell);
    });
    return map;
  }, [flightPlan.cells]);

  // Nákladové sloupce ve stabilním pořadí — jinak by se po každé mutaci přeházely.
  const costTickets = useMemo(() => {
    const tickets = [...(draft?.tickets ?? [])];
    tickets.sort((a, b) => {
      const byKind = (KIND_ORDER[a.kind] ?? 9) - (KIND_ORDER[b.kind] ?? 9);
      return byKind !== 0 ? byKind : sequenceOf(a) - sequenceOf(b);
    });
    return tickets;
  }, [draft]);

  // Popisek nákupní jednotky = její směrování. Obousměrný ticket dostane šipku tam i zpět, spojený let
  // se pozná podle `+` před nástupní stanicí.
  const ticketLabels = useMemo(() => {
    const labels = new Map<string, string>();
    const outboundLegs = outbound?.legs ?? [];
    const inboundLegs = inbound?.legs ?? [];

    outboundLegs.forEach((leg) => {
      const both = inboundLegs.some((other) => other.ticketId === leg.ticketId);
      labels.set(leg.ticketId, `${leg.fromCode ?? '—'} ${both ? '↔' : '→'} ${leg.toCode ?? '—'}`);
    });
    inboundLegs.forEach((leg) => {
      if (!labels.has(leg.ticketId)) {
        labels.set(leg.ticketId, `${leg.fromCode ?? '—'} → ${leg.toCode ?? '—'}`);
      }
    });
    connectedFlights.forEach((flight) => {
      labels.set(flight.ticketId, `+${flight.legs[0]?.fromCode ?? '—'}`);
    });
    (draft?.tickets ?? [])
      .filter((ticket) => ticket.kind === TransportSchemeTicketKind.Bus)
      .forEach((ticket) => labels.set(ticket.ticketId, t('transportScheme.matrix.bus')));

    return labels;
  }, [connectedFlights, draft, inbound, outbound, t]);

  /** Režim nákupu letenek per ticket — badge vedle směrování v záhlaví nákladového sloupce. */
  const ticketModes = useMemo(() => {
    const modes = new Map<string, RegularTransportMode>();
    [...(outbound?.legs ?? []), ...(inbound?.legs ?? [])].forEach((leg) =>
      modes.set(leg.ticketId, leg.mode)
    );
    connectedFlights.forEach((flight) => modes.set(flight.ticketId, flight.mode));
    return modes;
  }, [connectedFlights, inbound, outbound]);

  const legColumns = useMemo(() => {
    const columns: MatrixLegColumn[] = [];

    const push = (
      direction: TransportSchemeDirectionResponse | undefined,
      group: 'outbound' | 'inbound'
    ) => {
      direction?.legs.forEach((leg) => {
        columns.push({
          key: `${group}:${leg.ticketId}:${leg.facilityId}`,
          ticketId: leg.ticketId,
          facilityId: leg.facilityId,
          group,
          label: `${leg.fromCode ?? '—'}–${leg.toCode ?? '—'}`,
        });
      });
    };

    push(outbound, 'outbound');
    push(inbound, 'inbound');

    connectedFlights.forEach((flight) => {
      flight.legs.forEach((leg) => {
        columns.push({
          key: `connected:${leg.ticketId}:${leg.facilityId}`,
          ticketId: leg.ticketId,
          facilityId: leg.facilityId,
          group: 'connected',
          label: `${leg.fromCode ?? '—'}–${leg.toCode ?? '—'}`,
        });
      });
    });

    return columns;
  }, [connectedFlights, inbound, outbound]);

  if (!draft || !scheme || !outbound || !inbound) {
    return null;
  }

  const definitionReadOnly = readOnly || scheme.definitionReadOnly;
  // Spojená rotace musí umět dovézt do hubu cesty tam a odvézt z hubu cesty zpět. U kombinované
  // dopravy je jeden z těch hubů autobusová stanice, takže tam příplatek za rotaci nedává smysl.
  const isPureAir = draft.kind === TransportSchemeKind.Air;

  /**
   * Každý zásah do řetězu jde na server a může selhat. Bez hlášky by klik vypadal, jako by se nic
   * nestalo — a přesně tak se pozná i frontend mluvící se zastaralým backendem.
   */
  const runChainMutation = (action: Promise<void>) => {
    void action.catch((error: unknown) =>
      toast({
        severity: 'error',
        title: t('transportScheme.chain.mutationErrorTitle'),
        description: serverErrorDetail(error) ?? t('transportScheme.chain.mutationError'),
      })
    );
  };

  /** Odebrání se ptá, jen když na ticketu závisí ručně zadané ceny termínů — jinak maže rovnou. */
  const removeTicket = (ticketId: string) => {
    const ticket = ticketsById.get(ticketId);
    if ((ticket?.manualCostTermCount ?? 0) > 0) {
      setPendingRemoval({ ticketId, manualCostTermCount: ticket?.manualCostTermCount ?? 0 });
      return;
    }
    runChainMutation(editor.removeTicket(ticketId));
  };

  /**
   * Prohození se ptá, jen když se jím zahodí přiřazené trasy termínů. Kolik jich je, se zjišťuje až
   * teď a přímo ze serveru — trasu jde nastavit i v detailu termínu nebo v kalendáři, takže načtená
   * odpověď schématu ji vidět nemusí a uživatel by o ni přišel bez varování. Proto je klik
   * asynchronní.
   */
  const flipKind = async () => {
    setCheckingFlip(true);
    try {
      const impact = await editor.loadSwapImpact();
      if (!impact.canSwap) return;

      if (impact.assignedLineTermCount > 0) {
        setPendingFlip(impact.assignedLineTermCount);
        return;
      }

      await editor.flipKind();
    } catch (error) {
      toast({
        severity: 'error',
        title: t('transportScheme.chain.mutationErrorTitle'),
        description: serverErrorDetail(error) ?? t('transportScheme.chain.mutationError'),
      });
    } finally {
      setCheckingFlip(false);
    }
  };

  /**
   * Přiřazená trasa autobusového svozu pro jeden směr. Ukazuje se jen v rozsahu termínu — trasa patří
   * dvojici (termín, směr), takže na seriálu není co nastavovat.
   *
   * Editovatelnost neplyne z `definitionReadOnly`: trasa je vlastní termínu i tehdy, když jinak sdílí
   * definici seriálu. Zamyká ji až stav dopravní vrstvy (`readOnly`).
   */
  const termChainRow = (direction: TransportSchemeDirectionResponse) => {
    if (scope !== 'term' || !termId) return null;

    const which = direction.direction === 'Outbound' ? 'Outbound' : 'Inbound';
    const assigned = draft.termDirections.find(
      (row) => row.termId === termId && row.direction === which
    );

    const patch = (values: Partial<TermDirectionDraft>) => {
      editor.patchDraft({
        termDirections: assigned
          ? draft.termDirections.map((row) =>
              row.termId === termId && row.direction === which ? { ...row, ...values } : row
            )
          : [
              ...draft.termDirections,
              {
                termId,
                direction: which,
                stationChainId: null,
                stationChainName: null,
                stationChainReversed: false,
                ...values,
              },
            ],
      });
    };

    return (
      <div className="grid grid-cols-2 items-end gap-2">
        <StationChainSelector
          mode="single"
          label={t('transportScheme.bus.chain')}
          disabled={readOnly}
          selected={
            assigned?.stationChainId != null
              ? [
                  {
                    stationChainId: assigned.stationChainId,
                    name: assigned.stationChainName ?? '',
                    code: null,
                    isReturn: false,
                  },
                ]
              : []
          }
          onChange={(items: readonly StationChainRef[]) => {
            const picked = items[0];
            patch({
              stationChainId: picked?.stationChainId ?? null,
              stationChainName: picked?.name ?? null,
            });
          }}
        />
        <div className="pb-1.5">
          <Switch
            checked={assigned?.stationChainReversed ?? false}
            disabled={readOnly}
            onCheckedChange={(next) => patch({ stationChainReversed: next })}
            label={t('transportScheme.bus.reversed')}
          />
        </div>
      </div>
    );
  };

  const renderDirection = (direction: TransportSchemeDirectionResponse) => {
    if (direction.mode === TransportMode.Bus) {
      const key = direction.direction === 'Outbound' ? 'outbound' : 'inbound';
      const values = draft[key];
      return (
        <div className="flex flex-col gap-2 p-2">
          <div className="grid grid-cols-2 gap-2">
            <StationSelector
              mode="single"
              label={t('transportScheme.bus.station')}
              disabled={definitionReadOnly}
              selected={
                values.stationId != null
                  ? [
                      {
                        stationId: values.stationId,
                        name: values.stationName ?? '',
                        code: null,
                        isDomestic: false,
                        latitude: null,
                        longitude: null,
                      },
                    ]
                  : []
              }
              extraFilters={{ domestic: false }}
              onChange={(items: readonly StationRef[]) => {
                const picked = items[0];
                editor.patchDraft({
                  [key]: {
                    ...values,
                    stationId: picked?.stationId ?? null,
                    stationName: picked?.name ?? null,
                  },
                });
              }}
            />
            <TimeBox
              label={t('transportScheme.bus.time')}
              value={minutesToTime(values.timeMinutes)}
              disabled={definitionReadOnly}
              onChange={(next) =>
                editor.patchDraft({ [key]: { ...values, timeMinutes: timeToMinutes(next) } })
              }
            />
          </div>
          {termChainRow(direction)}
        </div>
      );
    }

    return (
      <AirChainTable
        legs={direction.legs}
        errors={direction.errors}
        ticketsById={ticketsById}
        coverage={flightPlan.coverage}
        readOnly={definitionReadOnly}
        onRemove={removeTicket}
      />
    );
  };

  const directionAction = (direction: TransportSchemeDirectionResponse) => {
    if (direction.mode === TransportMode.Bus || definitionReadOnly) return null;
    return (
      <TransportPickerButton
        fix={direction.fix}
        chainEndStationId={direction.legs.at(-1)?.toStationId ?? null}
        excludeIds={usedTransportIds}
        disabled={editor.isMutating}
        onAdd={(regularTransportId, insertAt) => {
          runChainMutation(
            editor.addTransport(
              regularTransportId,
              direction.direction === 'Outbound' ? 'Outbound' : 'Inbound',
              insertAt
            )
          );
        }}
      />
    );
  };

  return (
    <div className="flex flex-col gap-3">
      {scope === 'term' && onToggleOwnScheme ? (
        <SegmentedButtons
          value={scheme.isOwnScheme ? 'own' : 'shared'}
          options={[
            { value: 'shared', label: t('transportScheme.ownership.shared') },
            { value: 'own', label: t('transportScheme.ownership.own') },
          ]}
          onChange={(value) => onToggleOwnScheme(value === 'own')}
        />
      ) : null}

      <div className="op-dirs" data-swappable={scheme.canSwitchKind ? '' : undefined}>
        <DirectionPanel direction={outbound} action={directionAction(outbound)}>
          {renderDirection(outbound)}
        </DirectionPanel>

        {/* Kombinovaná doprava z legacy neříká, který směr je letecký. Prohození nepřepíná jen popisek —
            vymění i obsah obou stran, takže jde použít i na vyplněném schématu. */}
        {scheme.canSwitchKind ? (
          <button
            type="button"
            className="op-swap"
            disabled={definitionReadOnly || editor.isMutating || isCheckingFlip}
            title={t('transportScheme.kind.swapTo', {
              variant: t(
                `transportScheme.kind.${draft.kind === TransportSchemeKind.AirBus ? 'BusAir' : 'AirBus'}`
              ),
            })}
            aria-label={t('transportScheme.kind.swap')}
            onClick={() => void flipKind()}
          >
            <Icon name="swap" size={16} />
          </button>
        ) : null}

        <DirectionPanel direction={inbound} action={directionAction(inbound)}>
          {renderDirection(inbound)}
        </DirectionPanel>
      </div>

      {isPureAir ? (
        <ConnectedFlightsPanel
          flights={connectedFlights}
          hubStationId={outbound.legs[0]?.fromStationId ?? null}
          hubCode={outbound.legs[0]?.fromCode ?? null}
          coverage={flightPlan.coverage}
          excludeIds={usedTransportIds}
          readOnly={definitionReadOnly}
          disabled={editor.isMutating}
          onAdd={(regularTransportId) =>
            runChainMutation(editor.addConnectedFlight(regularTransportId))
          }
          onRemove={removeTicket}
        />
      ) : null}

      {/* Matice lícuje s okrajem panelu — vlastní odsazení má jen jeho patička s legendou. */}
      <Panel
        icon="calendar"
        title={t('transportScheme.matrix.title')}
        padded={false}
        footer={<TermFlightPlanMatrixLegend />}
      >
        <TermFlightPlanMatrix
          rows={draft.rows}
          tickets={costTickets}
          legColumns={legColumns}
          ticketLabels={ticketLabels}
          ticketModes={ticketModes}
          cells={cellsByKey}
          currency={draft.costCurrency}
          currencyOptions={CURRENCY_OPTIONS}
          sharedCapacity={draft.sharedCapacity}
          mode={scope === 'serie' ? 'serie' : scheme.isOwnScheme ? 'termOwn' : 'termShared'}
          readOnly={readOnly}
          isFlightPlanLoading={flightPlan.isLoading}
          onOpenTerm={onOpenTerm}
          onCurrencyChange={(currency) => editor.patchDraft({ costCurrency: currency })}
          // Společná hodnota se musí okamžitě promítnout do zděděných buněk — ty, které si termín
          // přepsal ručně, zůstávají beze změny.
          onSharedCapacityChange={(value) => {
            editor.patchDraft({
              sharedCapacity: value,
              rows: draft.rows.map((row) =>
                row.isOwnCapacity ? row : { ...row, capacity: value }
              ),
            });
          }}
          // Cena v definici. V seriálu je to společná hodnota, kterou zděděné buňky pod ní musí hned
          // převzít; u vlastní definice termínu je to rovnou jeho cena a zrcadlení drží řádek v souladu.
          onDefinitionCostChange={(ticketId, value) => {
            // Do kterého slotu částka patří, rozhoduje druh ticketu — autobus je cena za skupinu.
            const edited = draft.tickets.find((ticket) => ticket.ticketId === ticketId);
            if (!edited) return;
            const isGroup = isGroupPricedTicket(edited);

            editor.patchDraft({
              tickets: draft.tickets.map((ticket) =>
                ticket.ticketId === ticketId
                  ? isGroup
                    ? { ...ticket, expectedPaxAmount: null, expectedGroupAmount: value }
                    : { ...ticket, expectedPaxAmount: value, expectedGroupAmount: null }
                  : ticket
              ),
              rows: draft.rows.map((row) => ({
                ...row,
                costs: row.costs.map((cost) =>
                  cost.ticketId === ticketId && !cost.isManual
                    ? withAmountForKind(cost, edited, value)
                    : cost
                ),
              })),
            });
          }}
          onCostChange={(rowTermId, ticketId, value, isManual) => {
            const edited = draft.tickets.find((ticket) => ticket.ticketId === ticketId);
            if (!edited) return;

            editor.patchDraft({
              rows: draft.rows.map((row) =>
                row.termId === rowTermId
                  ? {
                      ...row,
                      costs: row.costs.map((cost) =>
                        cost.ticketId === ticketId
                          ? { ...withAmountForKind(cost, edited, value), isManual }
                          : cost
                      ),
                    }
                  : row
              ),
            });
          }}
          onCapacityChange={(rowTermId, value, isManual) => {
            editor.patchDraft({
              rows: draft.rows.map((row) =>
                row.termId === rowTermId
                  ? { ...row, capacity: value, isOwnCapacity: isManual }
                  : row
              ),
            });
          }}
        />
      </Panel>

      {pendingRemoval ? (
        <ConfirmDialog
          title={t('transportScheme.chain.removeConfirmTitle')}
          message={t('transportScheme.chain.removeConfirm', {
            count: pendingRemoval.manualCostTermCount,
          })}
          confirmLabel={t('transportScheme.chain.removeConfirmAction')}
          cancelLabel={t('transportScheme.chain.removeConfirmCancel')}
          onConfirm={() => {
            runChainMutation(editor.removeTicket(pendingRemoval.ticketId));
            setPendingRemoval(null);
          }}
          onCancel={() => setPendingRemoval(null)}
        />
      ) : null}

      {pendingFlip !== null ? (
        <ConfirmDialog
          title={t('transportScheme.kind.swapConfirmTitle')}
          message={t('transportScheme.kind.swapConfirm', { count: pendingFlip })}
          confirmLabel={t('transportScheme.kind.swapConfirmAction')}
          cancelLabel={t('transportScheme.kind.swapConfirmCancel')}
          onConfirm={() => {
            runChainMutation(editor.flipKind());
            setPendingFlip(null);
          }}
          onCancel={() => setPendingFlip(null)}
        />
      ) : null}
    </div>
  );
}

/** Řadicí klíč ticketu uvnitř jednoho druhu — cesta tam má přednost před cestou zpět. */
function sequenceOf(ticket: TransportSchemeTicketResponse): number {
  return ticket.outboundSequence ?? (ticket.inboundSequence ?? 0) + 100;
}

/** Minuty od půlnoci → `HH:mm` pro `TimeBox`. */
function minutesToTime(minutes: number | null): string {
  if (minutes == null) return '';
  return `${String(Math.floor(minutes / 60)).padStart(2, '0')}:${String(minutes % 60).padStart(2, '0')}`;
}

function timeToMinutes(value: string): number | null {
  if (!value) return null;
  const [hours, minutes] = value.split(':').map(Number);
  if (Number.isNaN(hours) || Number.isNaN(minutes)) return null;
  return hours * 60 + minutes;
}
